import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EmailIcon from '@mui/icons-material/Email';
import SensorsIcon from '@mui/icons-material/Sensors';
import WebIcon from '@mui/icons-material/Web';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import VideoChatIcon from '@mui/icons-material/VideoChat';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AssessmentIcon from '@mui/icons-material/Assessment';
import styles from './AppShowcase.module.css';

interface AppInfo {
	name: string;
	icon: React.ReactNode;
	tagline: string;
	description: string;
	link: string;
}

const apps: AppInfo[] = [
	{
		name: 'CANdispatch',
		icon: <EmojiTransportationIcon className={styles.mainIcon} />,
		tagline: 'Patrol Management | Security',
		description: 'Effortlessly manage your patrol guards with real-time tracking, reporting, and analytics. Ensure accountability and streamline security operations.',
		link: 'https://streamdata.com/candispatch',
	},
	{
		name: 'CANtrack',
		icon: <TrackChangesIcon className={styles.mainIcon} />,
		tagline: 'Project Management | CRM',
		description: 'Organize tasks, log timesheets, and access real-time analytics — all in one intuitive, CRM-enhanced platform.',
		link: 'https://streamdata.com/cantrack',
	},
	{
		name: 'CANmail',
		icon: <EmailIcon className={styles.mainIcon} />,
		tagline: 'Smarter Email Marketing',
		description: `
			Send, track, and manage mail with ease. CANmail unites your entire email marketing workflow into one easy-to-use, compliant, and powerful platform.
		`,
		link: 'https://streamdata.com/canmail',
	},
	{
		name: 'CANsense',
		icon: <SensorsIcon className={styles.mainIcon} />,
		tagline: 'Business Health | Infrastructure & Devices Monitoring',
		description: 'Real-time system monitoring, health checks, and automated alerts for critical infrastructure. Your mission-critical monitoring subsystem.',
		link: 'https://streamdata.com',
	},
	{
		name: 'CANwebsite',
		icon: <WebIcon className={styles.mainIcon} />,
		tagline: 'Custom CANapps powered website',
		description: 'Launch a scalable, SEO-optimized website tailored to your brand. Enhance engagement with AI tools and boost conversions.',
		link: 'https://streamdata.com/canwebsite',
	},
	{
		name: 'CANpark',
		icon: <DirectionsCarIcon className={styles.mainIcon} />,
		tagline: 'Parking Enforcement',
		description: 'Smarter parking enforcement. Bring your guards, admins, and property managers onto one real-time platform for streamlined oversight.',
		link: 'https://streamdata.com/canpark',
	},
	{
		name: 'CANmeet',
		icon: <VideoChatIcon className={styles.mainIcon} />,
		tagline: 'Video Confrencing Platform',
		description: 'A video conferencing platform to effortlessly schedule, execute and manage meetings. Powered by AI notes.',
		link: 'https://streamdata.com',
	},
	{
		name: 'CANai',
		icon: <PsychologyIcon className={styles.mainIcon} />,
		tagline: 'Talk to your data | AI Business Intelligence',
		description: 'A custom AI integration, tailored to your data. No more messing around with technical software to understand your data. With CANai, you just ask!',
		link: 'https://streamdata.com/caninsight',
	},
	{
		name: 'CANstore',
		icon: <StorefrontIcon className={styles.mainIcon} />,
		tagline: 'eCommerce Platform',
		description: 'A comprehensive online shopping platform. Customize your storefront, manage inventory, and process payments from one secure hub.',
		link: 'https://streamdata.com/canstore',
	},
	{
		name: 'CANreports',
		icon: <AssessmentIcon className={styles.mainIcon} />,
		tagline: 'Analytics Dashboard',
		description: 'Unleash the power of data-driven decisions. Access real-time analytics on traffic, user behavior, and conversions.',
		link: 'https://streamdata.com',
	},
];

const AppShowcase: React.FC = () => {
	const [currentIndex, setCurrentIndex] = useState(() => Math.floor(Math.random() * apps.length));
	const [isTransitioning, setIsTransitioning] = useState(false);

	useEffect(() => {
		const interval = setInterval(() => {
			setIsTransitioning(true);
			setTimeout(() => {
				setCurrentIndex((prev) => (prev + 1) % apps.length);
				setIsTransitioning(false);
			}, 400);
		}, 6000);

		return () => clearInterval(interval);
	}, []);

	const currentApp = apps[currentIndex];

	return (
		<div className={styles.showcase}>
			{/* Decorative elements */}
			<div className={styles.decorativeLine1} />
			<div className={styles.decorativeLine2} />
			<div className={styles.decorativeCircle} />

			<div className={styles.content}>
				<div className={`${styles.mainContent} ${isTransitioning ? styles.fadeOut : styles.fadeIn}`}>
					{/* Icon */}
					<Box className={styles.iconContainer}>
						{currentApp.icon}
					</Box>

					{/* Tagline */}
					<Typography className={styles.tagline}>
						{currentApp.tagline}
					</Typography>

					{/* App name as main heading */}
					<Typography variant="h2" className={styles.appName}>
						{currentApp.name}
					</Typography>

					{/* Description */}
					<Typography className={styles.description}>
						{currentApp.description}
					</Typography>

					{/* Learn More Button */}
					<Button
						className={styles.learnMoreBtn}
						endIcon={<NorthEastIcon />}
						href={currentApp.link}
						target="_blank"
						rel="noopener noreferrer"
					>
						Learn more
					</Button>
				</div>

				{/* Progress indicators */}
				<div className={styles.indicatorSection}>
					<div className={styles.indicators}>
						{apps.map((app, index) => (
							<button
								key={index}
								className={`${styles.indicator} ${index === currentIndex ? styles.active : ''}`}
								onClick={() => {
									if (index !== currentIndex) {
										setIsTransitioning(true);
										setTimeout(() => {
											setCurrentIndex(index);
											setIsTransitioning(false);
										}, 400);
									}
								}}
								aria-label={`View ${app.name}`}
							/>
						))}
					</div>
					<Typography className={styles.counter}>
						{String(currentIndex + 1).padStart(2, '0')} / {String(apps.length).padStart(2, '0')}
					</Typography>
				</div>
			</div>

			{/* Footer */}
			<div className={styles.footer}>
				<Typography className={styles.copyright}>
					© 2026 Stream Data Systems. All rights reserved.
				</Typography>
			</div>
		</div>
	);
};

export default AppShowcase;
